import React, { useEffect, useState } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from './lib/supabaseClient';
import NavigationMenu from './components/Navigation/NavigationMenu';
import Breadcrumbs from './components/Navigation/Breadcrumbs';
import Footer from './components/Footer';
import HomePage from './components/HomePage';
import LoginPage from './components/Auth/LoginPage';
import EmployeeDashboard from './components/dashboards/EmployeeDashboard';
import WholesaleDashboard from './components/dashboards/WholesaleDashboard';
import ClientDashboard from './components/dashboards/ClientDashboard';
import OrderDetails from './components/OrderDetails';
import OrderHistory from './components/OrderHistory';
import BusinessProfile from './components/BusinessProfile';
import PersonalProfile from './components/PersonalProfile';
import AboutPage from './components/AboutPage';
import ServicesPage from './components/ServicesPage';
import PricingPage from './components/PricingPage';
import ContactsPage from './components/ContactsPage';
import BlogPage from './components/BlogPage';
import BlogArticle from './components/BlogArticle';
import BlogEditor from './components/BlogEditor';
import NotFound from './components/NotFound';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import TelegramWidget from './components/TelegramWidget';
import OrderDeliveryPage from './components/OrderDeliveryPage';
import QualityInspectionPage from './components/services/QualityInspectionPage';
import RegisterPage from './components/Auth/RegisterPage';
import SearchAndPurchasePage from './components/services/SearchAndPurchasePage';
import LogisticsPage from './components/services/LogisticsPage';
import PaymentPage from './components/services/PaymentPage';

function App() {
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Check authentication status on mount and when auth state changes
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const isAuthenticatedLocal = localStorage.getItem('isAuthenticated') === 'true';
      setIsAuthenticated(!!session || isAuthenticatedLocal);
    };

    checkAuth();

    // Subscribe to auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAuthenticated(!!session || localStorage.getItem('isAuthenticated') === 'true');
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Redirect authenticated users trying to access auth pages
  useEffect(() => {
    const path = location.pathname;
    if (isAuthenticated && (path === '/login' || path === '/register')) {
      window.location.href = '/client/dashboard';
    }
  }, [isAuthenticated, location]);

  React.useEffect(() => {
    // Initialize Yandex.Metrika
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.innerHTML = `(function(m,e,t,r,i,k,a){m[i]=m[i]||function(){(m[i].a=m[i].a||[]).push(arguments)};
m[i].l=1*new Date();
for (var j = 0; j < document.scripts.length; j++) {if (document.scripts[j].src === r) { return; }}
k=e.createElement(t),a=e.getElementsByTagName(t)[0],k.async=1,k.src=r,a.parentNode.insertBefore(k,a)})
(window, document, "script", "https://mc.yandex.ru/metrika/tag.js", "ym");

ym(100176049, "init", {
  clickmap:true,
  trackLinks:true,
  accurateTrackBounce:true,
  webvisor:true
});`;
    document.head.appendChild(script);

    // Add noscript fallback
    const noscript = document.createElement('noscript');
    noscript.innerHTML = `<div><img src="https://mc.yandex.ru/watch/100176049" style="position:absolute; left:-9999px;" alt="" /></div>`;
    document.body.appendChild(noscript);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
      if (noscript.parentNode) {
        noscript.parentNode.removeChild(noscript);
      }
    };
  }, []); // Initialize once

  // Track page views
  React.useEffect(() => {
    if (window.ym) {
      window.ym(100176049, 'hit', window.location.pathname + window.location.search + window.location.hash, {
        title: document.title
      });
    }
  }, [location]); // Track on route change

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Helmet>
        <meta charSet="utf-8" />
        <title>997 Cargo — Умная логистика и доставка из Китая | Быстро, надёжно, безопасно</title>
        <meta name="theme-color" content="#0171E3" />
        <meta name="author" content="997cargo" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://997cargo.ru/" />
      </Helmet>
      
      <NavigationMenu />
      <main className="flex-grow">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/register" element={<RegisterPage />} />
          
          {/* Employee Routes */}
          <Route path="/employee/dashboard" element={<EmployeeDashboard />} />
          
          {/* Wholesale Routes */}
          <Route path="/wholesale/dashboard" element={<WholesaleDashboard />} />
          
          {/* Client Routes */}
          <Route path="/client/dashboard" element={<ClientDashboard />} />
          
          {/* Common Routes */}
          <Route path="/orders/:id" element={<OrderDetails />} />
          <Route path="/order-history" element={<OrderHistory />} />
          <Route path="/profile/business" element={<BusinessProfile />} />
          <Route path="/profile/personal" element={<PersonalProfile />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/services/business/forwarding" element={<QualityInspectionPage />} />
          <Route path="/services/business/search" element={<SearchAndPurchasePage />} />
          <Route path="/services/business/logistics" element={<LogisticsPage />} />
          <Route path="/services/business/payment" element={<PaymentPage />} />
          <Route path="/prices" element={<PricingPage />} />
          <Route path="/contacts" element={<ContactsPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:id" element={<BlogArticle />} />
          <Route path="/blog/new" element={<BlogEditor />} />
          <Route path="/blogredactor" element={<BlogEditor />} />
          <Route path="/blog/edit/:id" element={<BlogEditor isEdit />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/order-delivery" element={<OrderDeliveryPage />} />
          <Route path="/tariffs" element={<div className="px-6"><h1>Тарифы</h1></div>} />
          <Route path="/payment" element={<div className="px-6"><h1>Оплата</h1></div>} />
          <Route path="/warehouse" element={<div className="px-6"><h1>Адрес склада</h1></div>} />
          <Route path="/support" element={<div className="px-6"><h1>Поддержка</h1></div>} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <TelegramWidget />
      <Footer />
    </div>
  );
}

export default App;